import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Colors } from 'planoplan-ui-kit/core/colors';
import { Mixins } from 'planoplan-ui-kit/core/mixins';
import { ScrollLock } from '@libs';
import { Points, zIndex } from '@styles';

const IMAGES_PATH = '/images/mainpage';

const RightArrow = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M1.5 12H22.5M16.5 19.5L22.5 12L16.5 4.5" stroke="#0E0E0E" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LeftArrow = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M22.5 12H1.5M7.5 19.5L1.5 12L7.5 4.5" stroke="#0E0E0E" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const AuthorsLightbox = ({ item, locale = 'ru', onClose = () => {}, onPrev = () => {}, onNext = () => {} }) => {
  const imageRef = useRef(null);
  const [imageWidth, setImageWidth] = useState(null);
  const [, , jpg2x, webp2x] = item.srcset;

  const updateImageWidth = useCallback(() => {
    const image = imageRef.current;

    if (!image) return;

    const { width } = image.getBoundingClientRect();

    setImageWidth(width ? Math.round(width) : null);
  }, []);

  useEffect(() => {
    ScrollLock.lock(document.body);

    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft') onPrev();
      if (event.key === 'ArrowRight') onNext();
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      ScrollLock.unlock(document.body);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose, onPrev, onNext]);

  useEffect(() => {
    setImageWidth(null);
  }, [item?.srcset[0]]);

  useEffect(() => {
    window.addEventListener('resize', updateImageWidth);

    return () => window.removeEventListener('resize', updateImageWidth);
  }, [updateImageWidth]);

  if (!item) return null;

  const stopPropagation = (event) => event.stopPropagation();
  const author = locale === 'ru' ? item.author.author_ru : item.author.author_en;
  const caption = locale === 'ru' ? item.caption_ru : item.caption_en;
  const [ava1x, ava2x] = item.author.ava;

  return (
    <View onClick={onClose}>
      <Arrow
        $side="left"
        onClick={(event) => {
          stopPropagation(event);
          onPrev();
        }}
        aria-label="Previous">
        <LeftArrow />
      </Arrow>
      <Arrow
        $side="right"
        onClick={(event) => {
          stopPropagation(event);
          onNext();
        }}
        aria-label="Next">
        <RightArrow />
      </Arrow>

      <Container>
        <Wrapper>
          <ImageWrapper>
            <Picture>
              <source type="image/webp" srcSet={`${IMAGES_PATH}/${webp2x}`} />
              <img ref={imageRef} src={`${IMAGES_PATH}/${jpg2x}`} alt={item.author.alt} onLoad={updateImageWidth} />
            </Picture>
          </ImageWrapper>
          <Info $width={imageWidth}>
            <Avatar
              src={`${IMAGES_PATH}/${ava1x}`}
              srcSet={`${IMAGES_PATH}/${ava1x} 1x, ${IMAGES_PATH}/${ava2x} 2x`}
              alt={author}
            />
            <Texts>
              <Author>{author}</Author>
              <Caption>{caption}</Caption>
            </Texts>
          </Info>
        </Wrapper>
      </Container>
    </View>
  );
};

const View = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: ${zIndex('modal')};
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(14, 14, 14, 0.95);
`;

const Container = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 70px 16px 24px;

  @media ${Points.tablet_landscape} {
    padding: 40px 90px;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
`;

const ImageWrapper = styled.div`
  min-width: 0;
  min-height: 0;
  max-width: 100%;
  overflow: hidden;
`;

const Picture = styled.picture`
  display: block;
  width: 100%;
  height: 100%;

  img {
    display: block;
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
  }
`;

const Info = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: ${(p) => (p.$width ? `${p.$width}px` : 'fit-content')};
  max-width: 100%;
  margin-top: 16px;
`;

const Avatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

const Texts = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  color: ${Colors.white};
`;

const Author = styled.span`
  font-weight: 500;
  font-size: 14px;
  line-height: 1.3;
`;

const Caption = styled.span`
  font-size: 12px;
  line-height: 1.3;
  color: ${Colors.light_gray};
`;


const Arrow = styled.button`
  ${Mixins.resetButton()};
  ${Mixins.transition('opacity')};
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: ${(p) => (p.$side === 'left' ? '10px' : 'auto')};
  right: ${(p) => (p.$side === 'right' ? '10px' : 'auto')};
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  background-color: ${Colors.white};
  border-radius: 14px;
  border: 1px solid ${Colors.main_dark}  
  cursor: pointer;

  &:hover {
    opacity: 0.7;
  }

  @media ${Points.tablet_landscape} {
    left: ${(p) => (p.$side === 'left' ? '24px' : 'auto')};
    right: ${(p) => (p.$side === 'right' ? '24px' : 'auto')};
  }
`;
